import React, { useEffect, useState, useRef } from "react";
import Modal from "@mui/material/Modal";
import { RxCross1 } from "react-icons/rx";
import { Formik } from "formik";
import * as Yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import { HttpService } from "../../../service/HttpService";
import { toast } from "react-toastify";
import "../../Plant/View/View.scss";
import "../../../pages/Plant/PlantListAdd/Plant.scss";
import { IoMdAdd } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import CircularProgress from "@mui/material/CircularProgress";
import {
  FormControl,
  Select,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { RiEyeLine } from "react-icons/ri";

function RockwoolSchedule({
  open,
  handleClose,
  schedule_task_id,
  fetchSchedule,
}) {
  const httpService = new HttpService();
  const [cultivars, setCultivars] = useState([]);
  const [matricIdsMap, setMatricIdsMap] = useState({});
  const [loadingMatricIds, setLoadingMatricIds] = useState({});
  const [trayDetailsMap, setTrayDetailsMap] = useState({});
  const [subTab, setSubTab] = useState(0);
  const [openIdsModalIndex, setOpenIdsModalIndex] = useState(null);
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [existingByTab, setExistingByTab] = useState({});

  // Draft cache per tab so switching tabs preserves unsaved entries
  const [tabDrafts, setTabDrafts] = useState({
    zerotoseven: null,
    eighttofourteen: null,
    fifteentotwentyone: null,
  });

  const formikRef = useRef(null);

  const getTabKey = (tabIndex) => {
    if (tabIndex === 0) return "zerotoseven";
    if (tabIndex === 1) return "eighttofourteen";
    return "fifteentotwentyone";
  };

  const handleTabChange = (_, newValue) => {
    // Save current draft for active tab before switching
    try {
      const currentKey = getTabKey(subTab);
      const currentValues = formikRef.current?.values;
      if (currentValues && currentValues.items) {
        setTabDrafts((prev) => ({ ...prev, [currentKey]: currentValues }));
      }
    } catch (_) {}
    setSubTab(newValue);
    
    // Fetch existing data for the new tab
    const newKey = getTabKey(newValue);
    if (!existingByTab[newKey] || existingByTab[newKey].length === 0) {
      fetchTabData(newKey);
    }
  };

  const fetchCultivars = async () => {
    try {
      const res = await httpService.get("plant/get_plant");
      setCultivars(res?.data?.data || []);
    } catch (e) {
      setCultivars([]);
    }
  };

  const fetchMatricIds = async (cultivarId, index, setFieldValue, values) => {
    if (!cultivarId) return;
    const key = getTabKey(subTab);
    const compoundKey = `${key}_${index}`;
    setLoadingMatricIds((prev) => ({
      ...prev,
      [compoundKey]: true,
    }));
    const currentSubTab = subTab;
    try {
      let endpoint = "";

      if (currentSubTab === 0) {
        // Call Cluster API
        endpoint = `motherplant/cluster-trays/${cultivarId}?tab_type=${key}&schedule_task_id=${schedule_task_id}`;
      } else {
        // Call Clone API
        endpoint = `motherplant/clone-trays/${cultivarId}?tab_type=${key}&schedule_task_id=${schedule_task_id}`;
      }
      const res = await httpService.get(endpoint);
      const trays = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setMatricIdsMap((prev) => ({
        ...prev,
        [compoundKey]: trays,
      }));
      if (setFieldValue && values) {
        // Auto-select first available tray and apply counts immediately using fresh trays
        const firstTrayId = trays?.[0]?.tray_id || "";
        setFieldValue(`items.${index}.tray_id`, firstTrayId);
        setFieldValue(`items.${index}.no_of_clusters`, 0);
        if (firstTrayId) {
          applyCountsFromTrayList(firstTrayId, index, setFieldValue, trays);
        }
      }
    } catch (e) {
      setMatricIdsMap((prev) => ({
        ...prev,
        [compoundKey]: [],
      }));
    } finally {
      setLoadingMatricIds((prev) => ({
        ...prev,
        [compoundKey]: false,
      }));
    }
  };

  // New: derive clone/cluster counts from tray list API (motherplant/tray_id/:cultivarId)
  const applyCountsFromTrayList = (
    trayId,
    index,
    setFieldValue,
    sourceTrays,
  ) => {
    const key = getTabKey(subTab);
    // Prefer provided trays (fresh from API) to avoid state update delays
    const trays = Array.isArray(sourceTrays)
      ? sourceTrays
      : matricIdsMap[`${key}_${index}`] || [];
    const selected = trays.find((t) => t?.tray_id === trayId);

    const clusterCount =
      selected?.cluster_count != null
        ? parseInt(selected.cluster_count, 10)
        : undefined;
    const cloneCount =
      selected?.clone_count != null
        ? parseInt(selected.clone_count, 10)
        : undefined;

    const derived = {
      no_of_clusters: clusterCount,
      no_of_clones: cloneCount,
      // If API provides ids on tray object, persist them for the IDs modal
      cluster_ids: Array.isArray(selected?.cluster_ids)
        ? selected.cluster_ids
        : undefined,
      clone_ids: Array.isArray(selected?.clone_ids)
        ? selected.clone_ids
        : undefined,
    };

    setTrayDetailsMap((prev) => ({
      ...prev,
      [`${key}_${index}`]: { ...(prev?.[`${key}_${index}`] || {}), ...derived },
    }));

    if (setFieldValue) {
      if (subTab === 0) {
        setFieldValue(`items.${index}.no_of_clusters`, clusterCount ?? 0);
      } else {
        setFieldValue(`items.${index}.no_of_clusters`, cloneCount ?? 0);
      }
    }
  };

  const ensureIdsAndOpenModal = async (index) => {
    try {
      const formValues = formikRef.current?.values;
      const key = getTabKey(subTab);
      const row = formValues?.items?.[index];
      const trayId = row?.tray_id;
      if (!trayId) {
        toast.error("Please select tray first.", {
          position: "top-right",
        });
        return;
      }

      // Check if we already have rockwool_tasks from existing data
      const existingData = existingByTab[key]?.find(item => item.tray_id === trayId);
      if (existingData?.rockwool_tasks) {
        const list = existingData.rockwool_tasks.map(task => ({
          clone_id: task.clone_id,
          cluster_id: task.cluster_id,
          rooting_status: task.rooting_status
        }));
        
        setTrayDetailsMap((prev) => ({
          ...prev,
          [`${key}_${index}`]: {
            ...(prev?.[`${key}_${index}`] || {}),
            ...(subTab === 0 ? { cluster_ids: list } : { clone_ids: list }),
          },
        }));
        setOpenIdsModalIndex(index);
        return;
      }

      // Fallback to old API if no existing data
      const endpoint =
        subTab === 0
          ? `rockwool/clusters/${trayId}?tab_type=${key}`
          : `rockwool/clones/${trayId}?tab_type=${key}`;
      const res = await httpService.get(endpoint);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
          ? res.data
          : [];

      setTrayDetailsMap((prev) => ({
        ...prev,
        [`${key}_${index}`]: {
          ...(prev?.[`${key}_${index}`] || {}),
          ...(subTab === 0 ? { cluster_ids: list } : { clone_ids: list }),
        },
      }));

      setOpenIdsModalIndex(index);
    } catch (_) {
      setOpenIdsModalIndex(index);
    }
  };

  const fetchTabData = async (tabKey) => {
    if (!schedule_task_id) return;
    setLoadingAssignments(true);
    try {
      const res = await httpService.get(
        `rockwool/existing?tab_type=${tabKey}&schedule_task_id=${schedule_task_id}`
      );
      const items = res?.data || [];
      setExistingByTab((prev) => ({
        ...prev,
        [tabKey]: items,
      }));
    } catch (e) {
      console.error(`Error fetching ${tabKey}:`, e);
      setExistingByTab((prev) => ({
        ...prev,
        [tabKey]: [],
      }));
    } finally {
      setLoadingAssignments(false);
    }
  };

  const fetchExistingAssignments = async () => {
    if (!schedule_task_id) return;
    setLoadingAssignments(true);
    try {
      // Fetch data for all three tabs
      const keys = ["zerotoseven", "eighttofourteen", "fifteentotwentyone"];
      const grouped = {
        zerotoseven: [],
        eighttofourteen: [],
        fifteentotwentyone: [],
      };
      
      // Fetch each tab's data
      await Promise.all(
        keys.map(async (key) => {
          try {
            const res = await httpService.get(
              `rockwool/existing?tab_type=${key}&schedule_task_id=${schedule_task_id}`
            );
            const items = res?.data || [];
            grouped[key] = items;
          } catch (e) {
            console.error(`Error fetching ${key}:`, e);
            grouped[key] = [];
          }
        })
      );

      // Flatten all items for existingAssignments
      const allItems = [...grouped.zerotoseven, ...grouped.eighttofourteen, ...grouped.fifteentotwentyone];
      setExistingAssignments(allItems);
      setExistingByTab(grouped);
    } catch (e) {
      console.error("Error fetching existing assignments:", e);
      setExistingAssignments([]);
      setExistingByTab({
        zerotoseven: [],
        eighttofourteen: [],
        fifteentotwentyone: [],
      });
    } finally {
      setLoadingAssignments(false);
    }
  };

  useEffect(() => {
    fetchCultivars();
  }, []);

  // Reset state and fetch when modal opens for a given schedule_task_id
  useEffect(() => {
    if (!open || !schedule_task_id) return;
    // Fresh state for new open/task
    setSubTab(0);
    setTabDrafts({
      zerotoseven: null,
      eighttofourteen: null,
      fifteentotwentyone: null,
    });
    setExistingAssignments([]);
    setExistingByTab({
      zerotoseven: [],
      eighttofourteen: [],
      fifteentotwentyone: [],
    });
    setMatricIdsMap({});
    setTrayDetailsMap({});
    setLoadingMatricIds({});
    setOpenIdsModalIndex(null);
    fetchCultivars();
    fetchExistingAssignments();
  }, [open, schedule_task_id]);

  // Load existing assignments into form when they are fetched
  useEffect(() => {
    if (existingAssignments.length > 0) {
      // This will trigger Formik to reinitialize with new values
      // The getInitialValues function will return the existing assignments
      getInitialValues();
    }
  }, [existingAssignments]);

  // Hydrate current tab rows with tray options and counts when switching back
  useEffect(() => {
    const values = formikRef.current?.values;
    if (!values || !Array.isArray(values.items)) return;

    // After form is initialized, populate counts from existing data
    setTimeout(() => {
      const sv = formikRef.current?.values;
      if (!sv) return;
      sv.items.forEach((it, idx) => {
        if (it?.tray_id) {
          // Check if we have existing data with counts
          const key = getTabKey(subTab);
          const existingData = existingByTab[key]?.find(item => item.tray_id === it.tray_id);
          
          if (existingData) {
            // Use counts from existing data
            const count = subTab === 0 
              ? parseInt(existingData.cluster_count || 0, 10)
              : parseInt(existingData.clone_count || 0, 10);
            
            formikRef.current?.setFieldValue(`items.${idx}.no_of_clusters`, count);
            
            // Store rockwool_tasks for the IDs modal
            if (existingData.rockwool_tasks) {
              setTrayDetailsMap((prev) => ({
                ...prev,
                [`${key}_${idx}`]: {
                  ...(prev?.[`${key}_${idx}`] || {}),
                  no_of_clusters: subTab === 0 ? parseInt(existingData.cluster_count || 0, 10) : undefined,
                  no_of_clones: subTab !== 0 ? parseInt(existingData.clone_count || 0, 10) : undefined,
                  cluster_ids: subTab === 0 ? existingData.rockwool_tasks : undefined,
                  clone_ids: subTab !== 0 ? existingData.rockwool_tasks : undefined,
                },
              }));
            }
          }
        }
      });
    }, 100);
  }, [subTab, existingByTab]);

  // Re-apply counts when tray lists (matricIdsMap) update for the active tab - only for new entries
  useEffect(() => {
    const sv = formikRef.current?.values;
    if (!sv || !Array.isArray(sv.items)) return;
    
    sv.items.forEach((it, idx) => {
      const key = getTabKey(subTab);
      const trays = matricIdsMap[`${key}_${idx}`];
      // Only apply if we have trays and this is a NEW entry (not from existing data)
      const existingData = existingByTab[key]?.find(item => item.tray_id === it.tray_id);
      
      if (it?.tray_id && Array.isArray(trays) && trays.length && !existingData) {
        applyCountsFromTrayList(
          it.tray_id,
          idx,
          formikRef.current?.setFieldValue,
          trays,
        );
      }
    });
  }, [matricIdsMap, subTab, existingByTab]);

  const getSubHeader = () => {
    if (subTab === 0) return "Add Cluster";
    if (subTab === 1) return "Add Clones";
    if (subTab === 2) return "Add Clones";
    return "";
  };

  const getFieldLabel = () => {
    if (subTab === 0) return "No Of Clusters";
    if (subTab === 1) return "No Of Clones";
    if (subTab === 2) return "No Of Clones";
    return "No Of Clusters";
  };

  const getFieldValue = (index) => {
    const key = getTabKey(subTab);
    const details = trayDetailsMap[`${key}_${index}`];
    if (subTab === 0 && details?.no_of_clusters) {
      return details.no_of_clusters;
    } else if ((subTab === 1 || subTab === 2) && details?.no_of_clones) {
      return details.no_of_clones;
    } else {
      return 0; // Default fallback
    }
  };

  const getInitialValues = () => {
    const key = getTabKey(subTab);
    // Prefer draft if present
    if (tabDrafts[key]?.items?.length) {
      return tabDrafts[key];
    }

    const list = existingByTab[key] || [];
    if (list && list.length > 0) {
      return {
        items: list.map((assignment) => ({
          cultivar: assignment.plant_id || "",
          tray_id: assignment.tray_id || "",
          no_of_clusters:
            key === "zerotoseven"
              ? parseInt(assignment.cluster_count ?? 0, 10) || 0
              : parseInt(assignment.clone_count ?? 0, 10) || 0,
        })),
      };
    }

    return {
      items: [{ cultivar: "", tray_id: "", no_of_clusters: 0 }],
    };
  };

  const removeItem = (index, setFieldValue, values) => {
    const newItems = values.items.filter((_, i) => i !== index);
    setFieldValue("items", newItems);

    const activeKey = getTabKey(subTab);

    // Clean up and shift the maps for the active tab
    setMatricIdsMap((prev) => {
      const newMap = {};
      Object.keys(prev).forEach((k) => {
        if (k.startsWith(`${activeKey}_`)) {
          const idx = parseInt(k.split("_")[1], 10);
          if (idx < index) {
            newMap[k] = prev[k];
          } else if (idx > index) {
            newMap[`${activeKey}_${idx - 1}`] = prev[k];
          }
          // if idx === index, it is omitted (deleted)
        } else {
          // Keep other tabs' entries unchanged
          newMap[k] = prev[k];
        }
      });
      return newMap;
    });

    setTrayDetailsMap((prev) => {
      const newMap = {};
      Object.keys(prev).forEach((k) => {
        if (k.startsWith(`${activeKey}_`)) {
          const idx = parseInt(k.split("_")[1], 10);
          if (idx < index) {
            newMap[k] = prev[k];
          } else if (idx > index) {
            newMap[`${activeKey}_${idx - 1}`] = prev[k];
          }
          // if idx === index, it is omitted (deleted)
        } else {
          // Keep other tabs' entries unchanged
          newMap[k] = prev[k];
        }
      });
      return newMap;
    });

    setLoadingMatricIds((prev) => {
      const newMap = {};
      Object.keys(prev).forEach((k) => {
        if (k.startsWith(`${activeKey}_`)) {
          const idx = parseInt(k.split("_")[1], 10);
          if (idx < index) {
            newMap[k] = prev[k];
          } else if (idx > index) {
            newMap[`${activeKey}_${idx - 1}`] = prev[k];
          }
          // if idx === index, it is omitted (deleted)
        } else {
          newMap[k] = prev[k];
        }
      });
      return newMap;
    });
  };

  return (
    <>
      <Modal className="plant-modal" open={open} onClose={handleClose}>
        <div className="modal_block">
          {/* Main Header */}
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">Assign for Rockwool</h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1
                onClick={handleClose}
                style={{ cursor: "pointer", fontSize: "20px" }}
              />
            </div>
          </div>

          {/* Sub Tabs (Day Ranges) */}
          <Tabs
            value={subTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            sx={{
              paddingLeft: "20px",
              "& .MuiTabs-indicator": {
                backgroundColor: "#00A272",
                height: "3px",
              },
              "& .MuiTab-root": {
                color: "#A1A2A1",
                fontWeight: 400,
                fontSize: "16px",
                textTransform: "none",
                padding: "12px 20px",
                "&.Mui-selected": {
                  color: "#00A272",
                  fontWeight: 600,
                  backgroundColor: "#E6F6F1",
                  borderRadius: "4px 4px 0 0",
                },
              },
            }}
          >
            <Tab label="0-7 Days" />
            <Tab label="8-14 Days" />
            <Tab label="15-21 Days" />
          </Tabs>

          {/* Dynamic Sub Header */}
          <div className="modal_header mb-0">
            <h3 style={{ fontSize: "18px", fontWeight: "600" }}>
              {getSubHeader()}
            </h3>
            {loadingAssignments && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: "8px",
                }}
              >
                <CircularProgress size={16} />
                <span style={{ fontSize: "14px", color: "#666" }}>
                  Loading existing assignments...
                </span>
              </div>
            )}
          </div>

          {/* Formik Form */}
          <Formik
            key={subTab}
            innerRef={formikRef}
            enableReinitialize
            initialValues={getInitialValues()}
            validationSchema={Yup.object().shape({
              items: Yup.array().of(
                Yup.object().shape({
                  cultivar: Yup.string().required("Please Select Cultivar"),
                  tray_id: Yup.string().required("Please Select Tray ID"),
                }),
              ),
            })}
            onSubmit={async (values, { setSubmitting, validateForm }) => {
              const errors = await validateForm(values);
              if (Object.keys(errors).length > 0) {
                toast.error("Please fill all required fields.", {
                  position: "top-right",
                });
                setSubmitting(false);
                return;
              }
              try {
                // Save current tab draft prior to submit to include in combined payload
                const currentKey = getTabKey(subTab);
                setTabDrafts((prev) => ({ ...prev, [currentKey]: values }));

                // Build combined assignments from all drafts (including current tab)
                const keys = [
                  "zerotoseven",
                  "eighttofourteen",
                  "fifteentotwentyone",
                ];
                const combinedAssignments = [];

                // Build a lookup of existing rows per tab by tray_id for id carry-over
                const existingLookup = new Map();
                keys.forEach((k) => {
                  const list = existingByTab[k] || [];
                  (list || []).forEach((row) => {
                    const id =
                      row?.id ||
                      row?._id ||
                      row?.assignment_id ||
                      row?.rockwool_id;
                    const tray = row?.tray_id;
                    if (id && tray) {
                      existingLookup.set(`${k}::${tray}`, id);
                    }
                  });
                });

                keys.forEach((key) => {
                  const draft = (key === currentKey
                    ? values
                    : tabDrafts[key]) || { items: [] };
                  (draft.items || []).forEach((it) => {
                    if (!it?.cultivar || !it?.tray_id) return; // skip incomplete rows
                    const assignment = {
                      plant_mother_task_id: it.cultivar,
                      tray_id: it.tray_id,
                      tab_type: key,
                    };
                    const maybeId = existingLookup.get(`${key}::${it.tray_id}`);
                    if (maybeId) assignment.id = maybeId;
                    combinedAssignments.push(assignment);
                  });
                });

                const payload = {
                  schedule_task_id: schedule_task_id,
                  assignments: combinedAssignments,
                };
                console.log("payload : ", payload);

                // if (existingAssignments.length > 0) {
                //   await httpService.put(
                //     `rockwool/rockwool-assignment/${schedule_task_id}`,
                //     payload
                //   );
                // } else {

                await httpService.post(`rockwool/rockwool-assignment`, payload);
                // }
                toast.success("Rockwool Assigned Successfully", {
                  position: "top-right",
                });
                fetchSchedule && fetchSchedule();
                handleClose();
              } catch (error) {
                toast.error(
                  error?.response?.data?.error ||
                    error.message ||
                    "Something went wrong.",
                  {
                    position: "top-right",
                  },
                );
              }
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="modal_content">
                  {values.items.map((item, index) => (
                    <div
                      key={index}
                      className="row"
                      style={{ alignItems: "center" }}
                    >
                      {/* Cultivar Name */}
                      <div className="col-md-4">
                        <div
                          className="form_field_wrapper mb-0"
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <label>
                            Cultivar Name{" "}
                            <span style={{ color: "red" }}>*</span>
                          </label>
                          <FormControl fullWidth>
                            <Select
                              id={`items.${index}.cultivar`}
                              name={`items.${index}.cultivar`}
                              value={item.cultivar}
                              onChange={(e) => {
                                handleChange(e);
                                setFieldValue(`items.${index}.tray_id`, "");
                                setFieldValue(
                                  `items.${index}.no_of_clusters`,
                                  0,
                                );
                                fetchMatricIds(e.target.value, index);
                              }}
                              onBlur={handleBlur}
                              displayEmpty
                              sx={{
                                height: "40px",
                                backgroundColor: "white",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#C4C4C4",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#C4C4C4",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  { borderColor: "#C4C4C4" },
                              }}
                            >
                              <MenuItem value="">Select Cultivar</MenuItem>
                              {cultivars.map((cultivar) => (
                                <MenuItem key={cultivar.id} value={cultivar.id}>
                                  {cultivar.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <div style={{ minHeight: "22px" }}>
                            {touched.items?.[index]?.cultivar &&
                              errors.items?.[index]?.cultivar && (
                                <div style={{ color: "red" }}>
                                  {errors.items[index].cultivar}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      {/* Tray ID */}
                      <div className="col-md-5">
                        <div
                          className="form_field_wrapper mb-0"
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <label>Tray ID</label>
                          <FormControl fullWidth>
                            <Select
                              id={`items.${index}.tray_id`}
                              name={`items.${index}.tray_id`}
                              value={values.items[index].tray_id || ""}
                              disabled={!!loadingMatricIds[`${getTabKey(subTab)}_${index}`]}
                              onChange={(e) => {
                                handleChange(e);
                                applyCountsFromTrayList(
                                  e.target.value,
                                  index,
                                  setFieldValue,
                                  matricIdsMap[`${getTabKey(subTab)}_${index}`],
                                );
                              }}
                              onBlur={handleBlur}
                              displayEmpty
                              sx={{
                                height: "40px",
                                backgroundColor: "white",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#C4C4C4",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#C4C4C4",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  { borderColor: "#C4C4C4" },
                              }}
                              endAdornment={
                                loadingMatricIds[`${getTabKey(subTab)}_${index}`] ? (
                                  <CircularProgress size={20} />
                                ) : null
                              }
                            >
                              <MenuItem value="">Select Tray ID</MenuItem>
                              {(() => {
                                const tabKey = getTabKey(subTab);
                                const trays = matricIdsMap[`${tabKey}_${index}`] || [];
                                const current =
                                  values.items[index].tray_id || "";
                                const hasCurrent = current
                                  ? trays.some((t) => t?.tray_id === current)
                                  : true;
                                const items = [];
                                if (!hasCurrent && current) {
                                  items.push(
                                    <MenuItem
                                      key={`current-${current}`}
                                      value={current}
                                    >
                                      {current}
                                    </MenuItem>,
                                  );
                                }
                                return items.concat(
                                  trays.map((tray) => (
                                    <MenuItem
                                      key={tray.id ?? tray.tray_id}
                                      value={tray.tray_id}
                                    >
                                      {tray.tray_id}
                                    </MenuItem>
                                  )),
                                );
                              })()}
                            </Select>
                          </FormControl>
                          <div style={{ minHeight: "22px" }}>
                            {touched.items?.[index]?.tray_id &&
                              errors.items?.[index]?.tray_id && (
                                <div style={{ color: "red" }}>
                                  {errors.items[index].tray_id}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      {/* No of Clusters */}
                      <div className="col-md-2">
                        <div
                          className="form_field_wrapper mb-0"
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <label>{getFieldLabel()}</label>
                          <TextField
                            id={`items.${index}.no_of_clusters`}
                            name={`items.${index}.no_of_clusters`}
                            type="number"
                            value={
                              (values.items?.[index]?.no_of_clusters ?? "") !==
                              ""
                                ? values.items[index].no_of_clusters
                                : getFieldValue(index)
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            disabled={true}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                height: "40px",
                                backgroundColor: "white",
                                "& fieldset": { borderColor: "#C4C4C4" },
                                "&:hover fieldset": { borderColor: "#C4C4C4" },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#C4C4C4",
                                },
                                "&.Mui-disabled": {
                                  backgroundColor: "white",
                                  "& fieldset": { borderColor: "#C4C4C4" },
                                },
                              },
                              "& .MuiInputBase-input": {
                                height: "40px",
                                padding: "8px 14px",
                              },
                            }}
                            inputProps={{ min: 1, step: 1 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="view ids"
                                    onClick={() => {
                                      ensureIdsAndOpenModal(index);
                                    }}
                                    edge="end"
                                    size="small"
                                  >
                                    <RiEyeLine size={18} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <div style={{ minHeight: "22px" }}></div>
                        </div>
                      </div>

                      {/* Delete Button */}
                      <div className="col-md-1">
                        <div
                          className="form_field_wrapper mb-0"
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              removeItem(index, setFieldValue, values)
                            }
                            style={{
                              background: "none",
                              border: "none",
                              color: "#ff4444",
                              cursor: "pointer",
                              padding: "8px",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            title="Delete row"
                          >
                            <MdDelete size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      onClick={() =>
                        setFieldValue("items", [
                          ...values.items,
                          { cultivar: "", tray_id: "", no_of_clusters: 0 },
                        ])
                      }
                      className="btn btn-border  add-btn-wrap"
                      style={{
                        padding: "8px 30px 6px 20px",
                        fontSize: "15px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontWeight: 400,
                        gap: "4px",
                      }}
                    >
                      <IoMdAdd className="mr-2" /> Add
                    </button>
                  </div>
                </div>

                <div className="modal_footer">
                  <button
                    onClick={handleClose}
                    className="btn btn-border"
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Assign
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </Modal>
      {/* IDs Modal */}
      <Modal
        open={openIdsModalIndex !== null}
        onClose={() => setOpenIdsModalIndex(null)}
      >
        <div
          className="modal_block"
          style={{ maxWidth: "361px", backgroundColor: "#fff" }}
        >
          <div className="modal_header" style={{ marginBottom: "unset" }}>
            <div className="modal_title_wrapper">
              <h2 className="modal_title">
                {subTab === 0 ? "Cluster IDs" : "Clone IDs"}
              </h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1
                onClick={() => setOpenIdsModalIndex(null)}
                style={{ cursor: "pointer", fontSize: "16px" }}
              />
            </div>
          </div>
          <div
            className="modal_content"
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              padding: "20px 20px",
            }}
          >
            {(openIdsModalIndex !== null
              ? subTab === 0
                ? trayDetailsMap[`${getTabKey(subTab)}_${openIdsModalIndex}`]?.cluster_ids || []
                : trayDetailsMap[`${getTabKey(subTab)}_${openIdsModalIndex}`]?.clone_ids || []
              : []
            ).map((entry, i) => {
              const displayValue =
                entry && typeof entry === "object"
                  ? entry.cluster_id ||
                    entry.clone_id ||
                    entry.id ||
                    entry.name ||
                    JSON.stringify(entry)
                  : entry;
              return (
                <div
                  key={i}
                  style={{
                    padding: "14px 16px",
                    borderRadius: "4px",
                    background: "#F5FAFF",
                    border: "1px solid #EDF2F7",
                    color: "#222",
                    fontFamily: "wf_light",
                  }}
                >
                  {displayValue}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </>
  );
}

export default RockwoolSchedule;
